import { memo, useRef, useState, useEffect, SyntheticEvent } from 'react'

import {
  playAudioAndCatchAbortError,
  calculatePercentageOf
} from '../../helpers'

import SvgCircle from 'common-components/SvgCircle'
import PlayIcon from 'assets/svgr-icons/Play.svg'

import * as Styled from './styled'
import { getAudioPlayerIconsSize } from './utils'

interface IProps {
  src: string
  currentPlayingAudioRef?: React.MutableRefObject<HTMLAudioElement | null>
  playIconSize?: number
  resumeIconSize?: number
  volume?: number
  className?: string
  customCircleSize?: number
  circleStrokeWidth?: number
  progressStrokeColor?: string
}

export const AudioCirclePlayer: React.FC<IProps> = props => {
  const {
    src,
    currentPlayingAudioRef,
    playIconSize = 12,
    resumeIconSize = 12,
    volume,
    className = '',
    customCircleSize,
    circleStrokeWidth = 10,
    progressStrokeColor = 'rgba(79, 220, 111, 1)'
  } = props
  const { circleSize, resetIconSize } = getAudioPlayerIconsSize(
    playIconSize,
    resumeIconSize
  )

  const audioRef = useRef<HTMLAudioElement | null>(null)

  const [progress, setProgress] = useState(0)

  const onTimeUpdate = (event: SyntheticEvent<HTMLAudioElement>) => {
    if (Number.isNaN(event.currentTarget.duration)) {
      return
    }
    const currentPercent = calculatePercentageOf(
      event.currentTarget.currentTime,
      event.currentTarget.duration
    )
    setProgress(currentPercent)
  }

  const onAudioPlayingEnded = () => {
    setProgress(0)
    if (currentPlayingAudioRef) {
      currentPlayingAudioRef.current = null
    }
  }

  const resetPlaying = (ref: HTMLAudioElement | null) => {
    if (ref) {
      ref.pause()
      ref.currentTime = 0
      setProgress(0)
      if (currentPlayingAudioRef) {
        currentPlayingAudioRef.current = null
      }
    }
  }

  const startPlaying = () => {
    if (audioRef.current) {
      if (currentPlayingAudioRef && currentPlayingAudioRef.current) {
        resetPlaying(currentPlayingAudioRef.current)
      }
      if (currentPlayingAudioRef) {
        currentPlayingAudioRef.current = audioRef.current
      }

      playAudioAndCatchAbortError(audioRef.current)
    }
  }

  const onPlayOrPauseHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    if (progress) {
      resetPlaying(audioRef.current)
    } else {
      startPlaying()
    }
  }

  useEffect(() => {
    const currentAudio = audioRef.current
    //reset playing when src changed
    return () => {
      resetPlaying(currentAudio)
    }
    // TODO: @gev-davtyan-rf check if we need to add resetPlaying to the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  useEffect(() => {
    if (audioRef.current && volume !== undefined) {
      audioRef.current.volume = volume
    }
  }, [volume])

  return (
    <Styled.AudioCirclePlayerWrapper
      onClick={onPlayOrPauseHandler}
      size={customCircleSize || circleSize}
      className={className}
    >
      <SvgCircle
        progress={progress}
        strokeColor={progressStrokeColor}
        strokeWidth={circleStrokeWidth}
        size={customCircleSize || circleSize}
      />

      <audio
        ref={audioRef}
        src={src}
        controls
        style={{ display: 'none' }}
        onTimeUpdate={onTimeUpdate}
        onEnded={onAudioPlayingEnded}
        preload="none"
      ></audio>

      <Styled.AudioCirclePlayerControllers>
        {progress ? <Styled.ResumeIcon size={resetIconSize} /> : <PlayIcon />}
      </Styled.AudioCirclePlayerControllers>
    </Styled.AudioCirclePlayerWrapper>
  )
}

export default memo(AudioCirclePlayer)
